/**
 * `/projects` (PRD §4.4) — projects the caller may see.
 *
 * G-Admin IA (operator override Task 3): company-first — pick a company, then list its projects.
 * Companies are created only via the new-project form (inline). CompanyAdmin lists their company
 * projects directly (no picker). Year is a *view filter* only (PRD §3.1).
 *
 * Row actions (Edit / Archive / Delete) live here — the former `/projects/:id` Before-PDP shell
 * redirects straight to Live (Ticket 68). Pattern mirrors `CompanyRow`.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Building2, CalendarRange, Pencil, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BackLink } from "@/components/back-link";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { CompanyRow } from "@/components/companies/company-row";
import { ProjectForm } from "@/components/project-form";
import { bundeslandLabel, formatDate } from "@/lib/format";
import { describeError } from "@/lib/errors";
import { liveFromState, type LiveBackFrom } from "@/lib/live-back";
import { useQueryFlag, useQueryParam } from "@/lib/query-param";
import { useSession } from "@/lib/session";
import {
  filterArchived,
  useCompanies,
  useProjectLifecycle,
  useProjects,
  useUpdateProject,
} from "@/lib/tenancy";
import { berlinToday } from "@/shared/dates";
import { isYearInProject, unionYears } from "@/shared/years";
import type { ProjectSummary } from "@/shared/schemas/tenancy";

const ALL = "all";

type Pending = { action: "archive" | "delete" } | null;

export function ProjectsPage() {
  const { user } = useSession();
  const isGAdmin = user?.role === "grantonomy_admin";

  if (isGAdmin) return <GAdminProjectsPage />;
  return <CompanyAdminProjectsPage />;
}

/**
 * CompanyAdmin: projects for their company only — no company picker.
 * Read-only list: no show-archived, no Edit / Archive / Delete (matrix #3 → N).
 */
function CompanyAdminProjectsPage() {
  const [params, setParams] = useSearchParams();
  const [year, setYear] = useQueryParam("year", { fallback: ALL });
  const [search, setSearch] = useState("");

  // `?archived=1` must not become useful for CA — strip it if present.
  useEffect(() => {
    if (!params.has("archived")) return;
    const next = new URLSearchParams(params);
    next.delete("archived");
    setParams(next, { replace: true });
  }, [params, setParams]);

  const projects = useProjects({ archived: false });
  const listed = useMemo(() => projects.data ?? [], [projects.data]);
  const years = useMemo(() => yearsOf(listed), [listed]);
  const visible = listed
    .filter((project) => year === ALL || runsIn(project, Number(year)))
    .filter((project) => matchesSearch(project, search));
  const coldLoading = projects.isLoading && !projects.data;

  return (
    <div>
      <ProjectsHeader />
      <ProjectFilters
        year={year}
        onYearChange={setYear}
        years={years}
        showArchived={false}
        onShowArchived={() => undefined}
        showArchivedToggle={false}
        search={search}
        onSearchChange={setSearch}
      />
      <ProjectList
        coldLoading={coldLoading}
        visible={visible}
        showCompany={false}
        isGAdmin={false}
      />
    </div>
  );
}

/**
 * G-Admin: without `?company=` show the company picker; `?company=` redirects to company detail.
 */
function GAdminProjectsPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const companyId = params.get("company") ?? "";
  const [showArchived, setShowArchived] = useQueryFlag("archived");
  const [search, setSearch] = useState("");

  const companies = useCompanies({ archived: true });
  const companyList = filterArchived(companies.data ?? [], showArchived);
  const coldCompanies = companies.isLoading && !companies.data;

  if (companyId) {
    return <Navigate to={`/companies/${companyId}?tab=projects`} replace />;
  }

  return (
    <div>
      <BackLink to="/" data-testid="companies-list-back">
        {t("dashboardExtra.backToDashboard")}
      </BackLink>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
            {t("companies.kicker")}
          </div>
          <h1 className="mt-2 text-4xl">{t("companies.title")}</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            {t("projectsList.chooseCompanyIntro")}
          </p>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-4">
        <Input
          className="w-64"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("projectsList.searchCompanies")}
          aria-label={t("projectsList.searchCompanies")}
        />
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Checkbox
            checked={showArchived}
            onCheckedChange={(value) => setShowArchived(value === true)}
          />
          {t("companiesExtra.showArchived")}
        </label>
      </div>

      <div className="mt-6">
        {coldCompanies ? (
          <Skeleton className="h-24 w-full rounded-xl" />
        ) : companyList.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-10 text-center">
            <Building2 className="mx-auto size-6 text-muted-foreground" />
            <h2 className="mt-3 text-lg">{t("companies.emptyTitle")}</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
              {t("companies.emptyBody")}
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="companies-list">
            {companyList
              .filter((company) =>
                search.trim()
                  ? company.name.toLowerCase().includes(search.trim().toLowerCase())
                  : true,
              )
              .map((company) => (
                <li key={company.id}>
                  <CompanyRow company={company} selectTo={`/companies/${company.id}`} />
                </li>
              ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ProjectsHeader() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <div className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
          {t("projectsList.kicker")}
        </div>
        <h1 className="mt-2 text-4xl">{t("projectsList.title")}</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{t("projectsList.intro")}</p>
      </div>
    </div>
  );
}

function ProjectFilters({
  year,
  onYearChange,
  years,
  showArchived,
  onShowArchived,
  showArchivedToggle = true,
  search,
  onSearchChange,
  searchPlaceholder,
}: {
  year: string;
  onYearChange: (value: string) => void;
  years: number[];
  showArchived: boolean;
  onShowArchived: (value: boolean) => void;
  /** Company Admin has no show-archived control (Ticket 92). */
  showArchivedToggle?: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-8 flex flex-wrap items-center gap-4">
      <Input
        className="w-64"
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder={searchPlaceholder ?? t("projectsList.searchProjects")}
        aria-label={t("projectsList.searchProjects")}
      />
      <Select value={year} onValueChange={onYearChange}>
        <SelectTrigger className="w-40" aria-label={t("projectsList.yearFilter")}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t("projectsList.allYears")}</SelectItem>
          {years.map((value) => (
            <SelectItem key={value} value={String(value)}>
              {value}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {showArchivedToggle && (
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Checkbox
            checked={showArchived}
            onCheckedChange={(value) => onShowArchived(value === true)}
          />
          {t("projectsList.showArchived")}
        </label>
      )}
    </div>
  );
}

function ProjectList({
  coldLoading,
  visible,
  showCompany,
  isGAdmin,
}: {
  coldLoading: boolean;
  visible: ProjectSummary[];
  showCompany: boolean;
  isGAdmin: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-6 space-y-3">
      {coldLoading && <Skeleton className="h-24 w-full rounded-xl" />}
      {!coldLoading && visible.length === 0 && (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          {t("projectsList.empty")}
        </div>
      )}
      {visible.map((project) => (
        <ProjectRow
          key={project.id}
          project={project}
          showCompany={showCompany}
          isGAdmin={isGAdmin}
        />
      ))}
    </div>
  );
}

export function ProjectRow({
  project,
  showCompany = false,
  isGAdmin = false,
  liveFrom: liveFromOverride,
}: {
  project: ProjectSummary;
  showCompany?: boolean;
  /** Edit / Archive / restore / Delete are G-Admin only (matrix #3). */
  isGAdmin?: boolean;
  /** Override Live BACK target (e.g. company detail Projects tab). */
  liveFrom?: LiveBackFrom;
}) {
  const { t } = useTranslation();
  const update = useUpdateProject();
  const lifecycle = useProjectLifecycle();
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState<Pending>(null);
  const liveHref = `/projects/${project.id}/live`;
  const liveFrom: LiveBackFrom =
    liveFromOverride ?? (isGAdmin ? `/companies/${project.companyId}?tab=projects` : "/projects");
  const liveState = liveFromState(liveFrom);

  function run(action: "archive" | "restore" | "delete", messageKey: string) {
    lifecycle.mutate(
      { id: project.id, action },
      {
        onSuccess: () => {
          toast.success(t(messageKey));
          setPending(null);
        },
        onError: (error) => toast.error(describeError(error)),
      },
    );
  }

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-card p-5 shadow-soft"
      data-testid="project-row"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Link to={liveHref} state={liveState} className="truncate text-lg hover:underline">
            {project.name}
          </Link>
          {project.archived && <Badge variant="secondary">{t("projectsList.archivedBadge")}</Badge>}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          {showCompany && <span>{project.companyName}</span>}
          <span>{bundeslandLabel(project.bundesland)}</span>
          <span className="inline-flex items-center gap-1">
            <CalendarRange className="size-3.5" />
            {formatDate(project.startDate)} – {formatDate(project.endDate)}
          </span>
          <span className="inline-flex items-center gap-1">
            <Users className="size-3.5" />
            {t("projectsList.people", { count: project.personCount })}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="project-row-actions">
        <Button variant="outline" size="sm" asChild>
          <Link to={liveHref} state={liveState}>
            {t("common.open")}
          </Link>
        </Button>
        {isGAdmin && (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            <Pencil className="size-4" /> {t("projectOverview.editProject")}
          </Button>
        )}
        {isGAdmin &&
          (project.archived ? (
            <Button
              variant="outline"
              size="sm"
              disabled={lifecycle.isPending}
              onClick={() => run("restore", "projectForm.restored")}
            >
              {t("projectForm.restore")}
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setPending({ action: "archive" })}>
              {t("projectForm.archive")}
            </Button>
          ))}
        {isGAdmin && (
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => setPending({ action: "delete" })}
          >
            {t("common.delete")}
          </Button>
        )}
      </div>

      {isGAdmin && (
        <Dialog open={editing} onOpenChange={setEditing}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("projectForm.editTitle")}</DialogTitle>
              <DialogDescription>{t("projectForm.subtitle")}</DialogDescription>
            </DialogHeader>
            <ProjectForm
              lockCompany
              initial={{
                companyId: project.companyId,
                name: project.name,
                bsfzProjectDescription: project.bsfzProjectDescription ?? "",
                projectCode: project.projectCode ?? "",
                startDate: project.startDate,
                endDate: project.endDate,
              }}
              submitLabel={t("common.save")}
              busy={update.isPending}
              onCancel={() => setEditing(false)}
              onSubmit={(values) =>
                update.mutate(
                  {
                    id: project.id,
                    patch: {
                      name: values.name,
                      startDate: values.startDate,
                      endDate: values.endDate,
                      bsfzProjectDescription: values.bsfzProjectDescription || null,
                      projectCode: values.projectCode || null,
                    },
                  },
                  {
                    onSuccess: () => {
                      toast.success(t("projectForm.saved"));
                      setEditing(false);
                    },
                    onError: (error) => toast.error(describeError(error)),
                  },
                )
              }
            />
          </DialogContent>
        </Dialog>
      )}

      <ConfirmDialog
        open={pending?.action === "archive"}
        onOpenChange={(open) => !open && setPending(null)}
        title={t("adminExtra.archiveDialogTitle")}
        body={t("adminExtra.archiveDialogBody", { name: project.name })}
        confirmLabel={t("adminExtra.archiveDialogConfirm")}
        busy={lifecycle.isPending}
        onConfirm={() => run("archive", "projectForm.archived")}
      />
      <ConfirmDialog
        open={pending?.action === "delete"}
        onOpenChange={(open) => !open && setPending(null)}
        title={t("projectForm.deleteTitle")}
        body={t("projectForm.deleteBody", { name: project.name })}
        confirmLabel={t("common.delete")}
        typedConfirmation={t("projectForm.deleteConfirm")}
        typedHint={t("projectForm.deleteConfirmHint")}
        destructive
        busy={lifecycle.isPending}
        onConfirm={() => run("delete", "projectForm.deleted")}
      />
    </div>
  );
}

function matchesSearch(project: ProjectSummary, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return (
    project.name.toLowerCase().includes(needle) ||
    project.companyName.toLowerCase().includes(needle)
  );
}

/** Every year any project touches plus the current one, newest first. */
function yearsOf(projects: ProjectSummary[]): number[] {
  const thisYear = Number(berlinToday().slice(0, 4));
  const years = new Set<number>([thisYear, ...unionYears(projects, thisYear)]);
  return [...years].sort((a, b) => b - a);
}

function runsIn(project: ProjectSummary, year: number): boolean {
  return isYearInProject(year, project.startDate, project.endDate);
}
